import ExportCover from './Partials/ExportCover';
import ExportSummary from './Partials/ExportSummary';
import ExportDay from './Partials/ExportDay';
import ExportNav from './Partials/ExportNav';
import ExportPricing from './Partials/ExportPricing';
import ExportPricingDaily from './Partials/ExportPricingDaily';
import ExportPricingModeSwitcher from './Partials/ExportPricingModeSwitcher';
import ExportContents, {
  groupContentsByCategory,
} from './Partials/ExportContents';
import ExportPrograms, { getProgramGroups } from './Partials/ExportPrograms';
import ExportThemeSwitcher from './Partials/ExportThemeSwitcher';
import ExportShareButton from './Partials/ExportShareButton';
import ExportLanguageModal from './Partials/ExportLanguageModal';
import ExportProductAccessModal from './Partials/ExportProductAccessModal';
import exportFilename from './exportFilename';
import { FluidLayoutProvider } from './layout';
import getTripPricing, { CATEGORY_KEY_MAP, MODE_MAP } from './getTripPricing';
import {
  downscaleLiveImagesForPrint,
  NOTO_SANS_KHMER_REGULAR_WOFF2_BASE64,
} from '@koob/html-to-pdf';
import { useEffect, useMemo, useState } from 'react';
import {
  resolveTravelDiaryTheme,
  KOOB_DEFAULT_THEME,
  toCssVariables,
  buildGoogleFontsUrl,
} from '@koob/travel-diary-branding';
import { getSetting, getSettingLabel } from '@koob/utils';
import { useTranslation } from 'react-i18next';
import { decodeId } from 'utils';
import { useAsync } from 'react-async';
import {
  getDaysWithSelectedExtras,
  getExperiencesMap,
  getExtraExperienceIds,
} from './dayExtras';
import { formatDate, DATE_US } from '@koob/dates';

function adaptPublicDiaryPricing(pricing) {
  if (!pricing) return null;

  return {
    mode: MODE_MAP[pricing.displayMode] ?? 'items',
    currency: pricing.currency,
    travellerCount: pricing.travellerCount,
    perPersonTotal: pricing.amountPerTraveller,
    grandTotal: pricing.totalAmount,
    categories: pricing.categories?.map((category) => ({
      key: CATEGORY_KEY_MAP[category.category] ?? category.category,
      perPersonAmount: category.amountPerTraveller,
    })),
    items: pricing.items?.map((item) => ({
      category: CATEGORY_KEY_MAP[item.category] ?? item.category,
      label: item.serviceName,
      startDate: formatDate(item.startDate, DATE_US),
      endDate: formatDate(item.endDate, DATE_US),
      pax: item.pax,
      amount: item.amount,
    })),
  };
}

const PRINT_STYLES = `
  @media screen {
    .export-page {
      margin-bottom: 1px;
      box-shadow: 0 1px 0 0 var(--td-primary);
    }
  }

  @media screen and (max-width: 767px) {
    .export-page {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }
    #section-cover .export-page {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
    #section-cover .export-page .z-10 {
      padding: 24px !important;
    }
    .export-page .grid {
      grid-template-columns: 1fr !important;
      gap: 16px !important;
    }
    .export-page .grid > [class*='col-span-'] {
      grid-column: auto !important;
    }
    .export-page [class*='max-w-[155mm]'] {
      max-width: 100% !important;
    }
    .export-page .text-8xl {
      font-size: clamp(2.5rem, 12vw, 4rem) !important;
      line-height: 1 !important;
    }
    .export-page .text-5xl {
      font-size: clamp(2rem, 9vw, 3rem) !important;
    }
  }

  .export-stamp {
    display: inline-block;
    border: 1px solid var(--td-primary);
    color: var(--td-primary);
    padding: 4px 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .export-rich-text p {
    margin: 0 0 0.6em;
  }
  .export-rich-text h1,
  .export-rich-text h2,
  .export-rich-text h3,
  .export-rich-text h4,
  .export-rich-text h5,
  .export-rich-text h6 {
    margin: 0.9em 0 0.3em;
    font-family: var(--td-font-display);
    font-size: 1.15em;
    color: var(--td-ink);
  }
  .export-rich-text ul,
  .export-rich-text ol {
    margin: 0 0 0.6em 1.2em;
  }
  .export-rich-text ul {
    list-style: disc;
  }
  .export-rich-text ol {
    list-style: decimal;
  }
  .export-rich-text li {
    margin-bottom: 0.2em;
  }
  .export-rich-text img {
    max-width: 100%;
    height: auto;
    margin: 0.6em 0;
  }
  .export-rich-text a {
    color: var(--td-primary);
    text-decoration: underline;
  }
  .export-rich-text u {
    text-decoration: underline;
  }
  .export-rich-text s {
    text-decoration: line-through;
  }
  .export-rich-text > *:last-child {
    margin-bottom: 0;
  }

  .export-inclusion ul,
  .export-inclusion ol {
    list-style: none;
    margin-left: 0;
  }
  .export-inclusion li {
    position: relative;
    padding-left: 1.45em;
    margin-bottom: 0.55em;
  }
  .export-inclusion li::before {
    position: absolute;
    left: 0;
    top: 0;
  }
  .export-inclusion--included li::before {
    content: '✓';
    color: var(--td-primary);
  }
  .export-inclusion--excluded li {
    color: var(--td-ink-muted);
  }
  .export-inclusion--excluded li::before {
    content: '✕';
    opacity: 0.7;
  }

  .export-nav-scroll {
    scrollbar-width: thin;
    scrollbar-color: var(--td-ink-muted) transparent;
  }
  .export-nav-scroll::-webkit-scrollbar {
    height: 3px;
  }
  .export-nav-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .export-nav-scroll::-webkit-scrollbar-thumb {
    background: var(--td-ink-muted);
    border-radius: 9999px;
  }

  .export-stamp--light {
    border-color: rgba(255, 255, 255, 0.5);
    color: #ffffff;
  }

  .export-day-story > *:first-child::first-letter {
    float: left;
    margin: 4px 12px 0 0;
    font-family: var(--td-font-display);
    font-size: 56px;
    line-height: 0.8;
    color: var(--td-primary);
  }

  @media print {
    @page { size: A4 portrait; margin: 0; }
    body { visibility: hidden; }
    #export-target, #export-target * { visibility: visible; }
    #export-target { position: absolute; top: 0; left: 0; width: 100%; }
    #export-target,
    #export-target * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .export-hidden { display: none !important; }
    .export-page {
      break-after: page;
      page-break-after: always;
      margin-bottom: 0;
      box-shadow: none;
    }
  }
`;

export default function TripExport({
  trip,
  countries,
  tourOperator,
  experiences,
  hotels,
  accommodationLabels,
  images,
  locales,
  getTravelDiarySettings,
  // Injected, never imported: the two apps do not talk to the same endpoint
  // (`api/v1/experiences/:id` in to, `/experiences/:id` in dmc) and the public
  // diary reroutes through a share token. Importing one app's client into this
  // package would silently 404 in the others — same reason
  // `getTravelDiarySettings` is a prop.
  getExperience,
  langSelected,
  setLangSelected,
  showLanguageModal,
  setShowLanguageModal,
  shareUrl,
  shareDisabled = false,
  pricingSummary,
  pricing: precomputedPricing,
  travelDiaryContents,
  getOrganizationTheme,
  previewControls = false,
}) {
  const { t } = useTranslation('trips');
  const [processing, setProcessing] = useState(false);
  const [printLayout, setPrintLayout] = useState(false);
  const [previewPricingMode, setPreviewPricingMode] = useState(null);
  const [previewThemeKey, setPreviewThemeKey] = useState('to');
  const [productAccessDismissed, setProductAccessDismissed] = useState(false);

  const { data: themeData } = useAsync({
    promiseFn: getOrganizationTheme,
    organizationId: tourOperator?.id,
    skip: !getOrganizationTheme || !tourOperator?.id,
  });
  const tourOperatorTheme = resolveTravelDiaryTheme(themeData?.data);
  const themeOptions = [
    {
      key: 'to',
      name: tourOperator?.displayName ?? 'Tour Operator',
      theme: tourOperatorTheme,
    },
    { key: 'koob', name: 'KOOB', theme: KOOB_DEFAULT_THEME },
  ];
  const theme =
    (previewControls &&
      themeOptions.find((option) => option.key === previewThemeKey)?.theme) ||
    tourOperatorTheme;
  const baseThemeCssVariables = toCssVariables(theme);
  const themeCssVariables = {
    ...baseThemeCssVariables,
    '--td-font-display': `${baseThemeCssVariables['--td-font-display']}, 'Noto Sans Khmer'`,
    '--td-font-body': `${baseThemeCssVariables['--td-font-body']}, 'Noto Sans Khmer'`,
  };

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = buildGoogleFontsUrl(theme);
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.displayFont, theme.bodyFont]);

  // Products the DMC never distributed to this organization: the container marks
  // them `isAccessible: false` rather than fetching them (the API would 403).
  // They stay in the diary, but with no content and no price — the TO is told
  // about them up front instead of discovering the holes in the PDF.
  const inaccessibleProducts = useMemo(
    () => [
      ...(experiences ?? [])
        .filter((experience) => experience?.isAccessible === false)
        .map((experience) => ({
          key: `experience-${experience?.id}`,
          name: experience?.name,
          type: 'experience',
        })),
      ...(hotels ?? [])
        .filter((hotel) => hotel?.isAccessible === false)
        .map((hotel) => ({
          key: `hotel-${hotel?.id}`,
          name: hotel?.name ?? hotel?.displayName,
          type: 'hotel',
        })),
    ],
    [experiences, hotels],
  );

  // KD-3548 — the day a required or recommended transfer is taken does not end
  // where its program says, and the export has to say the same thing as the
  // composer. The retained options come back from the API as ids only, so their
  // experience details — the only place the transfer typology and the itinerary
  // positions live — are fetched here, once for the whole trip, rather than in
  // the leaf that prints the option list.
  const extraExperienceIds = useMemo(() => getExtraExperienceIds(trip), [trip]);
  const [extraExperiences, setExtraExperiences] = useState({});

  useEffect(() => {
    if (!getExperience || extraExperienceIds.length === 0) {
      setExtraExperiences({});
      return undefined;
    }

    let cancelled = false;

    Promise.all(
      extraExperienceIds.map(async (experienceId) => {
        try {
          const experience = await getExperience({
            experienceId,
            locale: langSelected?.value,
          });
          return [experienceId, experience?.data];
        } catch {
          // One unfetchable option — typically another organization's product,
          // which 403s — must never leave the whole export without its extras.
          return undefined;
        }
      }),
    ).then((entries) => {
      if (cancelled) {
        return;
      }

      setExtraExperiences(Object.fromEntries(entries.filter(Boolean)));
    });

    return () => {
      cancelled = true;
    };
  }, [extraExperienceIds, getExperience, langSelected?.value]);

  const daysWithSelectedExtras = useMemo(
    () => getDaysWithSelectedExtras(trip, extraExperiences),
    [trip, extraExperiences],
  );

  const experiencesMap = useMemo(
    () => getExperiencesMap(experiences),
    [experiences],
  );

  const { data } = useAsync({
    promiseFn: getTravelDiarySettings,
    filters: { locale: langSelected?.value },
    watch: langSelected,
    skip: !langSelected,
  });
  const settingsData = data?.data;

  const scrollToLoadAllContent = async () => {
    // Scroll through the page to trigger lazy-loaded content, recalculating height as content is loaded.
    const viewportHeight = window.innerHeight;
    const scrollStep = viewportHeight * 0.8; // Scroll 80% of viewport at a time

    while (
      window.scrollY + viewportHeight <
      document.documentElement.scrollHeight
    ) {
      window.scrollTo(0, window.scrollY + scrollStep);
      await new Promise((resolve) => setTimeout(resolve, 150));
    }

    // Ensure we reach the final bottom if the page grew during scrolling.
    window.scrollTo(0, document.documentElement.scrollHeight);
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Scroll back to top
    window.scrollTo(0, 0);
    await new Promise((resolve) => setTimeout(resolve, 200));
  };

  const waitForImagesToLoad = async () => {
    const images = Array.from(document.querySelectorAll('img'));
    const promises = images.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve; // Continue even if some images fail
      });
    });
    await Promise.all(promises);
  };

  const waitForFontsToReady = async () => {
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
  };

  // Inject the PDF-export Khmer font (embedded base64, same one Puppeteer will
  // use) into the live document so React's page-break measurements run with
  // the exact same font metrics as the server-side render. Otherwise the
  // baked-in pageOffsets become invalid once the cloned document reflows
  // server-side and the meal/position blocks get cut across pages.
  const ensureExportFontsLoaded = async () => {
    if (!document.getElementById('export-fonts-style')) {
      const style = document.createElement('style');
      style.id = 'export-fonts-style';
      style.textContent = `
        @font-face {
          font-family: 'Noto Sans Khmer';
          font-style: normal;
          font-weight: 400;
          font-display: block;
          src: url(data:font/woff2;base64,${NOTO_SANS_KHMER_REGULAR_WOFF2_BASE64}) format("woff2");
        }
      `;
      document.head.appendChild(style);
    }
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
    // the font swap reflows the layout.
    await new Promise((resolve) => setTimeout(resolve, 500));
  };

  const exportToPdf = async () => {
    setProcessing(true);
    setPrintLayout(true);

    // Load the PDF fonts in the live document *before* any measurement so
    // React's page-break offsets are computed with the exact metrics the
    // printed page will use.
    await ensureExportFontsLoaded();

    // Scroll through the whole document to trigger every lazy-loaded image and
    // map, then wait for them: window.print() captures the live DOM as-is, so
    // anything still loading would be missing from the PDF.
    await scrollToLoadAllContent();
    await waitForImagesToLoad();
    await waitForFontsToReady();

    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Swap the photos for downscaled data URIs before printing: window.print()
    // embeds the source bitmap at its natural resolution, so a full-res photo
    // shown 220px tall would otherwise bloat the PDF. Same CSS box, so the
    // page-break measurements above stay valid. Restored right after printing.
    const restoreImages = await downscaleLiveImagesForPrint(document);

    // The Save-as-PDF dialog proposes document.title as the file name: give it
    // the same "travelogue-<id>-<name>" the service download used, and restore
    // the app title once the dialog closes.
    const previousTitle = document.title;
    document.title = exportFilename(trip);

    const restore = () => {
      setPrintLayout(false);
      restoreImages();
      document.title = previousTitle;
      window.removeEventListener('afterprint', restore);
    };
    window.addEventListener('afterprint', restore);

    // Hand off to the browser's own Save-as-PDF. PRINT_STYLES turns the live
    setProcessing(false);
    window.print();

    // window.print() blocks in Chromium/Firefox, so the afterprint listener has
    // fired by here; the direct call covers browsers where it does not block.
    restore();
  };

  const handleLanguageSelect = (lang) => {
    setLangSelected(lang);
    setShowLanguageModal(false);
  };
  const initalLocale = locales?.find((locale) => locale.value === trip?.locale);

  const contentCategories = groupContentsByCategory(travelDiaryContents);
  const programGroups = getProgramGroups(trip, experiences);
  const wholeTripServices = (trip?.globalOptions ?? []).map((option) => ({
    option,
  }));
  const wholeTripPageIndex = programGroups.length - 1;
  const realPricing =
    adaptPublicDiaryPricing(precomputedPricing) ??
    getTripPricing(trip, pricingSummary);
  const pricing =
    realPricing && previewPricingMode
      ? { ...realPricing, mode: previewPricingMode }
      : realPricing;
  const programsNav =
    programGroups.length > 0
      ? [
          {
            id: 'section-programs',
            label:
              getSettingLabel(settingsData, 'included-excluded-services') ||
              'Included / Excluded Services',
          },
        ]
      : [];
  const menuFields = getSetting(settingsData, 'menu')?.sections;
  const rawDayTemplate = getSettingLabel(menuFields, 'menu-day') || 'D{n}';
  const dayTemplate = rawDayTemplate.includes('{n}')
    ? rawDayTemplate
    : `${rawDayTemplate}{n}`;
  const sections = [
    {
      id: 'section-summary',
      label:
        getSettingLabel(menuFields, 'menu-itinerary') ||
        t('export.nav.itinerary'),
    },
    ...(trip?.tripDays?.map((_, index) => ({
      id: `section-day-${index}`,
      label: dayTemplate.replace('{n}', index + 1),
    })) ?? []),
    ...(pricing
      ? [
          {
            id: 'section-pricing',
            label:
              getSettingLabel(menuFields, 'menu-price') ||
              t('export.nav.price'),
            covers: ['section-programs'],
          },
        ]
      : programsNav),
    ...contentCategories.map((category) => ({
      id: `section-content-${category.id}`,
      label: category.label,
    })),
  ];

  return (
    <>
      <style>{PRINT_STYLES}</style>

      <ExportLanguageModal
        open={showLanguageModal}
        setOpen={setShowLanguageModal}
        locales={locales}
        onSelectLanguage={handleLanguageSelect}
        defaultLocale={initalLocale}
      />

      {/* Chained behind the language modal so the two never stack: the TO picks
       * a language, then immediately sees what the diary will be missing. The
       * warning is for the TO only, never for the traveller on the public link. */}
      <ExportProductAccessModal
        open={
          previewControls &&
          !showLanguageModal &&
          !!langSelected &&
          !productAccessDismissed
        }
        onClose={() => setProductAccessDismissed(true)}
        products={inaccessibleProducts}
      />
      {/* The section/field labels all come from the travel-diary settings,
       * which are only fetched once a language is picked — rendering the
       * diary before that shows raw "undefined" labels behind the modal. */}
      {!langSelected ? null : (
        <>
          {processing && (
            <div className="export-hidden fixed inset-0 bg-white z-modal flex items-center justify-center">
              <div className="text-center">
                <i className="fa-regular fa-spinner-third fa-spin text-6xl text-orange-500 mb-4" />
                <div className="text-xl font-medium text-gray-800">
                  Generating PDF...
                </div>
                <div className="text-sm text-gray-600 mt-2">
                  Please wait while we prepare your document
                </div>
              </div>
            </div>
          )}

          <FluidLayoutProvider disabled={printLayout}>
            <div style={themeCssVariables}>
              <ExportNav
                tourOperator={tourOperator}
                sections={sections}
                onPrint={exportToPdf}
                printLabel={getSettingLabel(menuFields, 'menu-print-pdf')}
                processing={processing}
              />

              {previewControls && pricing && (
                <ExportPricingModeSwitcher
                  mode={pricing.mode}
                  onChange={setPreviewPricingMode}
                />
              )}

              {previewControls && (
                <ExportThemeSwitcher
                  options={themeOptions}
                  value={previewThemeKey}
                  onChange={setPreviewThemeKey}
                />
              )}

              <div
                id="export-target"
                className="bg-white"
                style={{ fontFamily: 'var(--td-font-body)' }}
              >
                {shareUrl && (
                  <div className="export-hidden fixed bottom-16 right-5 z-sticky">
                    <ExportShareButton
                      shareUrl={shareUrl}
                      disabled={shareDisabled}
                    />
                  </div>
                )}

                <div id="section-cover">
                  <ExportCover
                    trip={trip}
                    countries={countries}
                    tourOperator={tourOperator}
                    images={images}
                    settings={settingsData}
                  />
                </div>

                <div id="section-summary">
                  <ExportSummary
                    trip={trip}
                    experiences={experiences}
                    tourOperator={tourOperator}
                    settings={settingsData}
                    theme={theme}
                  />
                </div>

                {trip?.tripDays?.map((day, index) => (
                  <div id={`section-day-${index}`} key={index}>
                    <ExportDay
                      trip={trip}
                      tourOperator={tourOperator}
                      day={day}
                      index={index}
                      days={daysWithSelectedExtras}
                      experiencesMap={experiencesMap}
                      extraExperiences={extraExperiences}
                      getExperience={getExperience}
                      experience={experiences?.find(
                        (experience) => experience.id === day.experienceId,
                      )}
                      hotel={hotels?.find(
                        (hotel) =>
                          hotel.id.toString() ===
                          (decodeId(day.hotelId) ?? '').toString(),
                      )}
                      settings={settingsData}
                      accommodationLabels={accommodationLabels}
                      langSelected={langSelected?.value}
                      theme={theme}
                    />
                  </div>
                ))}

                {pricing && (
                  <div id="section-pricing">
                    <ExportPricing
                      trip={trip}
                      tourOperator={tourOperator}
                      pricing={pricing}
                      settings={settingsData}
                    />

                    {pricing.mode === 'daily' && (
                      <ExportPricingDaily
                        trip={trip}
                        tourOperator={tourOperator}
                        pricing={pricing}
                        settings={settingsData}
                        days={daysWithSelectedExtras}
                        experiences={experiences}
                        hotels={hotels}
                      />
                    )}
                  </div>
                )}

                {programGroups.length > 0 && (
                  <div id="section-programs">
                    {programGroups.map((group, index) => (
                      <ExportPrograms
                        key={`${group.experienceId}-${index}`}
                        trip={trip}
                        tourOperator={tourOperator}
                        settings={settingsData}
                        group={group}
                        index={index}
                        total={programGroups.length}
                        extraExperiences={extraExperiences}
                        wholeTripServices={
                          index === wholeTripPageIndex ? wholeTripServices : []
                        }
                      />
                    ))}
                  </div>
                )}

                {contentCategories.map((category) => (
                  <div id={`section-content-${category.id}`} key={category.id}>
                    <ExportContents
                      trip={trip}
                      tourOperator={tourOperator}
                      settings={settingsData}
                      category={category}
                    />
                  </div>
                ))}
              </div>
            </div>
          </FluidLayoutProvider>
        </>
      )}
    </>
  );
}
